import React, { useRef } from "react"
import ModalRouteContext from "../context/ModalRouteContext"

/**
 * Keeps the previous page mounted underneath the current one so a route can be
 * presented as an overlay instead of a page swap.
 *
 * Wired in from gatsby-browser/gatsby-ssr's `wrapPageElement`. A link opts in
 * with `<Link to="/post" state={{ modal: true }}>`; without that state this is
 * a no-op passthrough and every route behaves exactly as before (which is also
 * what a crawler, a direct URL, and a hard reload all get).
 *
 * The shape of the returned tree never changes — always a two-slot fragment,
 * background first — so React reconciles the background page in place instead
 * of tearing it down. Slot 0 holds the *same element object* while a modal is
 * open, which lets React bail out of re-rendering that subtree entirely: the
 * Video page's DOM, its scroll position and its <video> elements all survive
 * untouched, and the sheet slides up over a live page rather than a snapshot.
 *
 * `bg.current === null` means we have no page to overlay (a cold load straight
 * onto the URL — history.state survives a reload, so `state.modal` can be true
 * with nothing behind it). In that case the route falls back to a normal page.
 */
const ModalRoute = ({ element, location }) => {
  const bg = useRef(null)
  const isModal = !!(location.state && location.state.modal) && bg.current !== null

  if (!isModal) bg.current = element

  return (
    <>
      {bg.current}
      {isModal ? (
        <ModalRouteContext.Provider value={true}>
          {element}
        </ModalRouteContext.Provider>
      ) : null}
    </>
  )
}

export default ModalRoute
